import { z } from 'zod';
import { NIVEL_TURMA, PRESENCA_CHAMADA, } from '../dominio/index.js';
import { zData, zDiasDaSemana, zDuracaoMinutos, zHoraDaGrade } from './agenda.js';
import { camposDeModalidade, conferirModalidade, MODALIDADES_ALOCAVEIS, rotuloDaModalidade, zModalidade, zNomeDeModalidade, } from './modalidades.js';
import { zNomeDePessoa } from './alunos.js';
import { zId } from './primitivos.js';
/**
 * Os contratos de turmas — `#v-turmas`, `#m-turma`, `#m-chamada`,
 * `#m-conflito`, a aba "Alunos da turma" de `#m-evento` e as abas Turmas
 * e Presenças de `#m-aluno`.
 *
 * O que NÃO está aqui, e por quê: dias, horário, duração e quadra de uma
 * turma não são campos dela. A turma POSSUI uma série da agenda (documento
 * 06) e o horário é o da série — "turma sem série própria evita o problema
 * clássico de dois calendários que divergem". Quando a recepção arrasta a
 * série inteira na grade, a tela de Turmas já mostra o horário novo, sem
 * sincronização nenhuma. Os campos existem na ENTRADA (criar e mudar a
 * grade) e na SAÍDA (lidos da série), nunca guardados em duplicidade.
 *
 * Três regras daqui valem nos dois lados e por isso moram neste arquivo:
 *
 *   o semáforo de lotação     "até 5 verde, de 6 a 8 amarelo, de 9 a 10
 *                             vermelho" — proporcional à capacidade, que é
 *                             da turma e não constante
 *   o período da matrícula    semiaberto, [entrou_em, saiu_em): no dia da
 *                             transferência a pessoa já está na turma nova
 *   o risco de cancelamento   três faltas seguidas é alto; duas mais fatura
 *                             vencida é médio
 *
 * Ver: Arena_backend/Docs/plano/10-turmas-professores-chamada.md
 */
// ---------------------------------------------------------------------
// Vocabulário
// ---------------------------------------------------------------------
/**
 * `Suspensa` é pausa com as matrículas mantidas: as aulas dali em diante
 * saem da grade (a quadra fica livre) e voltam na reativação. `Encerrada`
 * é o fim — a série acaba e as matrículas saem junto.
 */
export const STATUS_TURMA = ['Ativa', 'Suspensa', 'Encerrada'];
/**
 * O rótulo das modalidades mora em `schemas/modalidades.ts` — turmas,
 * planos e a fila do site mostram o mesmo nome, e "Futevôlei" escrito em
 * três lugares divergiria no primeiro ajuste. Sai pelo mesmo barril
 * (`@arena/shared/schemas`), então quem importava daqui não muda.
 */
export const ROTULO_NIVEL_TURMA = {
    Iniciante: 'Iniciante',
    Intermediario: 'Intermediário',
    Avancado: 'Avançado',
};
export const ROTULO_PRESENCA = {
    Presente: 'Presente',
    Falta: 'Falta',
    Reposicao: 'Reposição',
};
/**
 * Por que a matrícula acabou — o "· transferência" da aba Turmas de
 * `#m-aluno`. `Transferencia` e `TurmaEncerrada` nascem dos casos de uso
 * próprios; a saída pedida pela recepção escolhe entre os dois últimos.
 */
export const MOTIVOS_DE_SAIDA = [
    'Transferencia',
    'TurmaEncerrada',
    'Desistencia',
    'Outro',
];
export const MOTIVOS_DE_SAIDA_PEDIDA = ['Desistencia', 'Outro'];
export const ROTULO_MOTIVO_DE_SAIDA = {
    Transferencia: 'transferência de turma',
    TurmaEncerrada: 'turma encerrada',
    Desistencia: 'desistência',
    Outro: 'outro motivo',
};
export const SITUACOES_NA_ESPERA = ['Aguardando', 'Promovida', 'Desistiu', 'Encerrada'];
/**
 * Reposição é MOVIMENTO, não contador: "um contador não responde de onde
 * veio nem para onde foi". Crédito por falta justificada, débito no uso,
 * estorno quando a chamada é refeita e o ajuste manual do Dono.
 */
export const TIPOS_DE_MOVIMENTO_DE_REPOSICAO = ['Credito', 'Debito'];
export const ORIGENS_DE_MOVIMENTO_DE_REPOSICAO = [
    'FaltaJustificada',
    'UsoEmAula',
    'Estorno',
    'Ajuste',
];
/** "Capacidade máxima de 10 alunos" — o padrão do protótipo, alterável em `#m-turma`. */
export const CAPACIDADE_PADRAO = 10;
export const CAPACIDADE_MAXIMA = 40;
/** O gráfico de `#v-turmas` e o de `#m-aluno`. */
export const SEMANAS_DE_FREQUENCIA = 8;
export const AULAS_NO_GRAFICO_DO_ALUNO = 8;
/**
 * O critério do protótipo: "três faltas seguidas acendem o alerta". Mora
 * em constante porque o documento avisa que o dono vai ajustar o critério
 * — e o risco é consulta, não coluna: mudar aqui muda a próxima leitura.
 */
export const FALTAS_SEGUIDAS_RISCO_ALTO = 3;
export const FALTAS_SEGUIDAS_RISCO_MEDIO = 2;
export const NIVEIS_DE_RISCO = ['alto', 'medio'];
// ---------------------------------------------------------------------
// Regras dos dois lados
// ---------------------------------------------------------------------
/**
 * "Futevôlei · Iniciante", "Futevôlei · Intermediário / Avançado".
 *
 * `modalidadeOutra` é o nome livre da modalidade personalizada: sem ele,
 * a turma de "ritmos" se chamaria "Outra · Iniciante".
 */
export function tituloDaTurma(modalidade, niveis, modalidadeOutra) {
    const ordenados = NIVEL_TURMA.filter((n) => niveis.includes(n));
    const rotulo = ordenados.map((n) => ROTULO_NIVEL_TURMA[n]).join(' / ');
    const nome = rotuloDaModalidade(modalidade, modalidadeOutra);
    return rotulo ? `${nome} · ${rotulo}` : nome;
}
/**
 * O semáforo de `#v-turmas`. O protótipo fala em números absolutos para
 * dez vagas — até 5, de 6 a 8, de 9 a 10 —, e a capacidade é da turma:
 * a mesma régua em proporção dá exatamente aqueles cortes com dez.
 */
export function lotacaoDaTurma(matriculados, capacidade) {
    const ocupacao = capacidade > 0 ? matriculados / capacidade : 1;
    if (ocupacao <= 0.5)
        return 'folgada';
    if (ocupacao <= 0.8)
        return 'media';
    return 'cheia';
}
export function rotuloDeLotacao(matriculados, capacidade) {
    if (matriculados >= capacidade)
        return 'Lotada · lista de espera aberta';
    const lotacao = lotacaoDaTurma(matriculados, capacidade);
    if (lotacao === 'cheia')
        return 'Quase cheia';
    if (lotacao === 'media')
        return 'Com vagas';
    return 'Bastante espaço';
}
/**
 * A matrícula vale no dia? O período é SEMIABERTO, `[entrou_em,
 * saiu_em)` — o mesmo `[)` da constraint no banco. É o que faz a
 * transferência de 02/06 não pôr a aluna nas duas chamadas daquele dia:
 * na turma antiga ela saiu em 02/06, na nova entrou em 02/06.
 */
export function matriculaNoDia(entrouEm, saiuEm, dia) {
    return entrouEm <= dia && (saiuEm === null || saiuEm > dia);
}
/**
 * Quantas faltas seguidas, contando da aula mais recente para trás.
 *
 * Só entra aula com chamada registrada — a cancelada (o feriado) não tem
 * chamada e não conta nem quebra a sequência. A falta JUSTIFICADA também
 * é pulada: quem avisou que ia viajar não está sumindo, e mandar a
 * "mensagem de retorno" para essa pessoa seria o alerta errado. Presença
 * e reposição quebram a sequência.
 */
export function faltasSeguidas(registrosDoMaisRecente) {
    let faltas = 0;
    for (const registro of registrosDoMaisRecente) {
        if (registro.estado !== 'Falta')
            break;
        if (!registro.justificada)
            faltas += 1;
    }
    return faltas;
}
/**
 * O risco de cancelamento, derivado — nunca coluna: "coluna denormalizada
 * ficaria velha entre chamadas".
 *
 * `faturaVencida` é `null` quando quem pergunta não lê cobranças (a fatura
 * é do documento 12, e a matriz é quem diz se o perfil a vê): sem a fonte,
 * o nível médio não acende — e a resposta diz que não sabe, em vez de
 * afirmar "em dia".
 */
export function nivelDeRisco(faltasEmSequencia, faturaVencida) {
    if (faltasEmSequencia >= FALTAS_SEGUIDAS_RISCO_ALTO)
        return 'alto';
    if (faltasEmSequencia >= FALTAS_SEGUIDAS_RISCO_MEDIO && faturaVencida === true)
        return 'medio';
    return null;
}
// ---------------------------------------------------------------------
// Peças
// ---------------------------------------------------------------------
const texto = (minimo, maximo, mensagem) => z
    .string({ required_error: mensagem })
    .trim()
    .min(minimo, mensagem)
    .max(maximo, `No máximo ${maximo} caracteres.`);
/** Os níveis da turma, na ordem do vocabulário, sem repetição. */
export const zNiveisDaTurma = z
    .array(z.enum(NIVEL_TURMA))
    .min(1, 'Escolha ao menos um nível.')
    .transform((niveis) => NIVEL_TURMA.filter((n) => niveis.includes(n)));
export const zCapacidade = z
    .number({ invalid_type_error: 'Informe a capacidade.' })
    .int('A capacidade é um número inteiro.')
    .min(1, 'A turma tem ao menos uma vaga.')
    .max(CAPACIDADE_MAXIMA, `No máximo ${CAPACIDADE_MAXIMA} alunos por turma.`);
// ---------------------------------------------------------------------
// Entrada — turmas
// ---------------------------------------------------------------------
/**
 * O corpo de `POST /turmas` — `#m-turma`.
 *
 * `quadraId` é opcional porque a ALOCAÇÃO FIXA (pendência P1) é a fonte da
 * quadra quando existe para aquele horário: sem quadra, o servidor usa a
 * da alocação; com uma quadra diferente da alocada, recusa.
 *
 * `inicio` é a data da primeira aula possível — sem ela, hoje (ou amanhã,
 * se o horário de hoje já passou).
 */
export const zNovaTurma = z
    .object({
    ...camposDeModalidade,
    niveis: zNiveisDaTurma,
    professorId: zId,
    quadraId: zId.optional(),
    diasSemana: zDiasDaSemana,
    horaInicio: zHoraDaGrade,
    duracaoMinutos: zDuracaoMinutos.default(60),
    capacidade: zCapacidade.default(CAPACIDADE_PADRAO),
    inicio: zData.optional(),
})
    .superRefine(conferirModalidade);
/**
 * O corpo de `PATCH /turmas/:id`.
 *
 * O que mexe na GRADE — dias, horário, duração, quadra, e também nível e
 * professor, que são o título e o responsável das aulas — vale de
 * `aPartirDe` em diante, e `aPartirDe` nunca é antes de amanhã: a aula de
 * hoje fica como está, e o passado da série é histórico. Capacidade não
 * mexe na grade e vale na hora.
 */
export const zAlteracaoDeTurma = z
    .object({
    modalidade: zModalidade.optional(),
    modalidadeOutra: zNomeDeModalidade.nullable().optional(),
    niveis: zNiveisDaTurma.optional(),
    professorId: zId.optional(),
    capacidade: zCapacidade.optional(),
    quadraId: zId.optional(),
    diasSemana: zDiasDaSemana.optional(),
    horaInicio: zHoraDaGrade.optional(),
    duracaoMinutos: zDuracaoMinutos.optional(),
    aPartirDe: zData.optional(),
})
    .refine((a) => Object.keys(a).some((chave) => chave !== 'aPartirDe'), {
    message: 'Nada para alterar.',
})
    .superRefine(conferirModalidade);
/** Suspender e encerrar valem a partir de hoje, e exigem o motivo. */
export const zMotivoDaTurma = z.object({
    motivo: texto(3, 200, 'Diga o motivo.'),
});
export const zReativacaoDeTurma = z.object({
    aPartirDe: zData.optional(),
});
// ---------------------------------------------------------------------
// Entrada — matrícula e lista de espera
// ---------------------------------------------------------------------
/**
 * `POST /turmas/:id/matriculas`. Com a turma cheia, a pessoa entra na
 * LISTA DE ESPERA — "o décimo primeiro entra na lista de espera com
 * posição" — e a resposta diz qual das duas coisas aconteceu.
 */
export const zNovaMatricula = z.object({
    alunoId: zId,
    data: zData.optional(),
});
/**
 * `POST /turmas/matriculas/:id/sair`. `data` é o PRIMEIRO DIA FORA da
 * turma — o `saiu_em` do período semiaberto. Hoje por padrão; amanhã,
 * quando a pessoa ainda fez a aula de hoje.
 */
export const zSaidaDaTurma = z.object({
    motivo: z.enum(MOTIVOS_DE_SAIDA_PEDIDA, {
        errorMap: () => ({ message: 'Escolha o motivo da saída.' }),
    }),
    observacao: z.string().trim().max(200, 'No máximo 200 caracteres.').optional(),
    data: zData.optional(),
});
/** Transferir é sair de uma e entrar na outra NA MESMA TRANSAÇÃO. */
export const zTransferencia = z.object({
    turmaDestinoId: zId,
    data: zData.optional(),
});
export const zSaidaDaEspera = z.object({
    motivo: z.string().trim().max(200, 'No máximo 200 caracteres.').optional(),
});
// ---------------------------------------------------------------------
// Entrada — chamada e reposições
// ---------------------------------------------------------------------
/**
 * Um aluno na chamada. `justificada` só existe na falta: é ela que dá
 * crédito de reposição.
 */
export const zRegistroDeChamada = z
    .object({
    alunoId: zId,
    estado: z.enum(PRESENCA_CHAMADA),
    justificada: z.boolean().default(false),
})
    .refine((r) => !r.justificada || r.estado === 'Falta', {
    message: 'Só a falta se justifica.',
    path: ['justificada'],
});
/**
 * `PUT /aulas/:marcacaoId/chamada` — a chamada INTEIRA da aula, sempre.
 *
 * Refazer é mandar a lista de novo: o servidor atualiza as linhas
 * (`(marcacao_id, aluno_id)` é único), apaga a de quem saiu da lista e
 * acerta as reposições. Todo matriculado no dia precisa estar na lista —
 * chamada pela metade é a falta que ninguém registrou.
 */
export const zChamada = z
    .object({
    registros: z.array(zRegistroDeChamada).max(80, 'Chamada grande demais.'),
})
    .superRefine((chamada, ctx) => {
    const vistos = new Set();
    chamada.registros.forEach((registro, i) => {
        if (vistos.has(registro.alunoId)) {
            ctx.addIssue({
                code: 'custom',
                path: ['registros', i, 'alunoId'],
                message: 'O mesmo aluno aparece duas vezes na chamada.',
            });
        }
        vistos.add(registro.alunoId);
    });
});
/** O ajuste manual do saldo — "por que ela tem 2?" precisa de resposta. */
export const zAjusteDeReposicao = z.object({
    alunoId: zId,
    tipo: z.enum(TIPOS_DE_MOVIMENTO_DE_REPOSICAO),
    motivo: texto(3, 200, 'Diga o motivo do ajuste.'),
});
export const zConsultaDeAulas = z.object({
    data: zData.optional(),
});
// ---------------------------------------------------------------------
// Entrada — professores
// ---------------------------------------------------------------------
/**
 * Nem todo professor tem login. Quem tem é ligado à CONTA (`usuarioId`) de
 * alguém com vínculo nesta arena — é por ela que o perfil Professor
 * reconhece "as próprias turmas".
 */
export const zNovoProfessor = z.object({
    nome: zNomeDePessoa,
    usuarioId: zId.nullable().optional(),
});
export const zAlteracaoDeProfessor = z
    .object({
    nome: zNomeDePessoa.optional(),
    usuarioId: zId.nullable().optional(),
    ativo: z.boolean().optional(),
})
    .refine((a) => Object.keys(a).length > 0, { message: 'Nada para alterar.' });
// ---------------------------------------------------------------------
// Entrada — alocação fixa (pendência P1)
// ---------------------------------------------------------------------
export const zAlocacaoFixa = z.object({
    diaSemana: z.number().int().min(0).max(6),
    hora: zHoraDaGrade,
    duracaoMinutos: zDuracaoMinutos.default(60),
    modalidade: z.enum(MODALIDADES_ALOCAVEIS),
    quadraId: zId,
});
/**
 * `PUT /alocacoes-fixas` — o "Salvar alocação" de `#m-conflito`.
 *
 * Cada `(dia, hora)` que aparece aqui é DEFINIDO por inteiro: o que havia
 * de alocação vigente nesse horário é substituído pelo que veio. Horários
 * que não aparecem não são tocados.
 */
export const zDefinicaoDeAlocacao = z
    .object({
    aPartirDe: zData.optional(),
    alocacoes: z.array(zAlocacaoFixa).min(1, 'Escolha ao menos uma alocação.').max(56),
})
    .superRefine((definicao, ctx) => {
    const porModalidade = new Set();
    const porQuadra = new Set();
    definicao.alocacoes.forEach((a, i) => {
        const modalidade = `${a.diaSemana}|${a.hora}|${a.modalidade}`;
        const quadra = `${a.diaSemana}|${a.hora}|${a.quadraId}`;
        if (porModalidade.has(modalidade)) {
            ctx.addIssue({
                code: 'custom',
                path: ['alocacoes', i, 'modalidade'],
                message: 'A mesma modalidade não ocupa duas quadras no mesmo horário.',
            });
        }
        if (porQuadra.has(quadra)) {
            ctx.addIssue({
                code: 'custom',
                path: ['alocacoes', i, 'quadraId'],
                message: 'A mesma quadra não recebe duas modalidades no mesmo horário.',
            });
        }
        porModalidade.add(modalidade);
        porQuadra.add(quadra);
    });
});
export const zConsultaDeSugestao = z.object({
    modalidade: z.enum(MODALIDADES_ALOCAVEIS),
});
//# sourceMappingURL=turmas.js.map